import toast from 'react-hot-toast';

/**
 * Error response structure from the server
 */
interface ServerError {
  message?: string;
  error?: string;
  errors?: Array<{
    field: string;
    message: string;
  }> | Record<string, { message: string }>;
}

/**
 * Parsed error information
 */
export interface ParsedError {
  message: string;
  fieldErrors?: Record<string, string>;
}

/**
 * Parse error response from API and extract meaningful information
 */
export const parseApiError = (error: any): ParsedError => {
  // Default error message
  let message = 'An unexpected error occurred. Please try again.';
  const fieldErrors: Record<string, string> = {};

  try {
    // Check if it's an axios error with response
    if (error.response?.data) {
      const data: ServerError = error.response.data;

      // Handle validation errors (array format from Joi)
      if (Array.isArray(data.errors)) {
        data.errors.forEach(err => {
          fieldErrors[err.field] = err.message;
        });
        message = 'Please correct the validation errors below.';
      }
      // Handle Mongoose validation errors (object format)
      else if (data.errors && typeof data.errors === 'object') {
        Object.entries(data.errors).forEach(([field, error]) => {
          if (typeof error === 'object' && error.message) {
            fieldErrors[field] = error.message;
          }
        });
        message = 'Please correct the validation errors below.';
      }
      // Handle simple error message
      else if (data.message) {
        message = data.message;
      }
      // Handle error string
      else if (data.error) {
        message = data.error;
      }
    }
    // Handle network errors
    else if (error.message) {
      if (error.message.includes('Network Error')) {
        message = 'Network error. Please check your connection and try again.';
      } else if (error.message.includes('timeout')) {
        message = 'Request timed out. Please try again.';
      } else {
        message = error.message;
      }
    }
  } catch (parseError) {
    console.error('Error parsing API error:', parseError);
  }

  return {
    message,
    fieldErrors: Object.keys(fieldErrors).length > 0 ? fieldErrors : undefined
  };
};

/**
 * Display error to user using toast notifications
 */
export const displayError = (error: any, customMessage?: string): ParsedError => {
  const parsed = parseApiError(error);
  
  // Use custom message if provided, otherwise use parsed message
  const displayMessage = customMessage || parsed.message;
  
  // Show main error message
  toast.error(displayMessage, {
    duration: 5000,
    position: 'top-right',
  });

  // Show field-specific errors if present
  if (parsed.fieldErrors) {
    Object.entries(parsed.fieldErrors).forEach(([field, message]) => {
      // Format field name for display (e.g., "contractStartDate" -> "Contract Start Date")
      const formattedField = field
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, str => str.toUpperCase())
        .trim();
      
      toast.error(`${formattedField}: ${message}`, {
        duration: 6000,
        position: 'top-right',
      });
    });
  }

  return parsed;
};

/**
 * Handle form submission errors with field-level error state
 */
export const handleFormError = (
  error: any,
  setFieldErrors?: (errors: Record<string, string>) => void,
  customMessage?: string
): ParsedError => {
  const parsed = displayError(error, customMessage);
  
  // Update field-level errors if setter is provided
  if (setFieldErrors && parsed.fieldErrors) {
    setFieldErrors(parsed.fieldErrors);
  }
  
  return parsed;
};

/**
 * Clear field errors
 */
export const clearFieldErrors = (
  setFieldErrors: (errors: Record<string, string>) => void
) => {
  setFieldErrors({});
};

/**
 * Get user-friendly error message for specific error codes
 */
export const getErrorMessage = (statusCode?: number): string => {
  switch (statusCode) {
    case 400:
      return 'Invalid request. Please check your input and try again.';
    case 401:
      return 'You are not authorized. Please log in and try again.';
    case 403:
      return 'You do not have permission to perform this action.';
    case 404:
      return 'The requested resource was not found.';
    case 409:
      return 'This action conflicts with existing data.';
    case 422:
      return 'The data provided could not be processed.';
    case 500:
      return 'A server error occurred. Please try again later.';
    case 503:
      return 'The service is temporarily unavailable. Please try again later.';
    default:
      return 'An unexpected error occurred. Please try again.';
  }
};
